import axios from 'axios';
import { API_BASE_URL } from '../config/constants';

// Create an axios instance for customer API calls
const customerAPI = axios.create({
  baseURL: `${API_BASE_URL}/customer/`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include authentication token
customerAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // Don't override Content-Type if it's already set in the request config
    // This is important for FormData uploads which need 'multipart/form-data'
    if (config.headers['Content-Type'] === 'multipart/form-data') {
      // For multipart/form-data, let the browser set the correct boundary
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Upload a customer file for background processing
 * @param {FormData} formData - FormData object containing the file
 * @returns {Promise} Response from the server
 */
export const uploadCustomerFile = async (formData) => {
  try {
    const response = await customerAPI.post('upload-file/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading customer file:', error);
    throw error;
  }
};

/**
 * Get all customers with optional filters
 * @param {Object} filters - Filter parameters
 * @returns {Promise} List of customers
 */
export const getAllCustomers = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    
    const response = await customerAPI.get(`?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching customers:', error);
    throw error;
  }
};

/**
 * Get customer statistics
 * @returns {Promise} Customer statistics
 */
export const getCustomerStats = async () => {
  try {
    const response = await customerAPI.get('stats/');
    return response.data;
  } catch (error) {
    console.error('Error fetching customer stats:', error);
    throw error;
  }
};

/**
 * Get upload task status
 * @param {string} taskId - The Celery task ID
 * @returns {Promise} Task status and progress information
 */
export const getUploadStatus = async (taskId) => {
  try {
    const response = await customerAPI.get(`upload-status/${taskId}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching upload status:', error);
    throw error;
  }
};

/**
 * Get available database fields for mapping
 * @returns {Promise} Available fields organized by group
 */
export const getAvailableFields = async () => {
  try {
    const response = await customerAPI.get('fields/available/');
    return response.data;
  } catch (error) {
    console.error('Error fetching available fields:', error);
    throw error;
  }
};

/**
 * Preview file columns and get suggested mapping
 * @param {File} file - The file to preview
 * @param {Object} options - Options for preview (mapping_mode, has_header)
 * @returns {Promise} File columns and suggested mapping
 */
export const previewFileColumns = async (file, options = {}) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    // Add mapping mode and header options
    if (options.mapping_mode) {
      formData.append('mapping_mode', options.mapping_mode);
    }
    if (options.has_header !== undefined) {
      formData.append('has_header', options.has_header ? 'true' : 'false');
    }
    
    const response = await customerAPI.post('fields/preview/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error previewing file:', error);
    throw error;
  }
};

/**
 * Validate field mapping
 * @param {Object} mapping - Field mapping object
 * @returns {Promise} Validation result
 */
export const validateFieldMapping = async (mapping) => {
  try {
    const response = await customerAPI.post('fields/validate-mapping/', {
      mapping
    });
    return response.data;
  } catch (error) {
    console.error('Error validating mapping:', error);
    throw error;
  }
};

/**
 * Analyze duplicates in mapped data before upload
 * @param {string} filePreviewId - The temp file preview ID
 * @param {Object} mapping - Field mapping object
 * @param {number} threshold - Similarity threshold (0.0-1.0)
 * @returns {Promise} Duplicate analysis result
 */
export const analyzeDuplicates = async (filePreviewId, mapping, threshold = 0.85) => {
  try {
    const response = await customerAPI.post('fields/analyze-duplicates/', {
      file_preview_id: filePreviewId,
      mapping,
      threshold
    });
    return response.data;
  } catch (error) {
    console.error('Error analyzing duplicates:', error);
    throw error;
  }
};

/**
 * Upload file with custom field mapping
 * @param {File} file - The file to upload
 * @param {Object} mapping - Field mapping object
 * @param {Object} metadata - Optional metadata
 * @returns {Promise} Upload result with task ID
 */
export const uploadWithMapping = async (file, mapping, metadata = {}) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mapping', JSON.stringify(mapping));
    if (Object.keys(metadata).length > 0) {
      formData.append('metadata', JSON.stringify(metadata));
    }
    
    const response = await customerAPI.post('upload-with-mapping/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading with mapping:', error);
    throw error;
  }
};

/**
 * Get available options for mailing metadata fields
 * @returns {Promise} Available options for dropdowns
 */
export const getMailingMetadataOptions = async () => {
  try {
    const response = await customerAPI.get('fields/mailing-metadata-options/');
    return response.data;
  } catch (error) {
    console.error('Error fetching mailing metadata options:', error);
    throw error;
  }
};

/**
 * Get list of uploaded files with their status and details
 * @param {number} page - Page number
 * @param {number} pageSize - Records per page
 * @param {string} status - Filter by status (optional)
 * @param {string} search - Search by file name (optional)
 * @returns {Promise} List of uploaded files
 */
export const getUploadedFiles = async (page = 1, pageSize = 20, status = null, search = null) => {
  try {
    const params = { page, page_size: pageSize };
    if (status) params.status = status;
    if (search) params.search = search;
    
    const response = await customerAPI.get('uploaded-files/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching uploaded files:', error);
    throw error;
  }
};

export default customerAPI;
